import { FC } from 'react';
import {
  FRONT_ANATOMY,
  BACK_ANATOMY,
  ANATOMY_VIEWBOX,
  AnatomyMuscle,
} from './muscleZoneMap';
import { ZONE_LABELS, getIntensityColor } from './bodyMapScale';
import styles from './AnatomyFigure.module.scss';

interface Props {
  view: 'front' | 'back';
  /** Zona canonica → intensità fastidio 1-5 (assente = nessun fastidio). */
  zoneIntensity: Map<string, number>;
  /**
   * Opzionale: zona → opacità del fill 0-1 (default 0.92). Usato dalla heatmap
   * team per codificare la diffusione (quanti atleti): più diffuso = più opaco.
   */
  zoneOpacity?: Map<string, number>;
  hoveredZone: string | null;
  onHoverZone: (zone: string | null) => void;
  /** Click/tap su una zona segnalabile (toggle a carico del chiamante). */
  onSelectZone?: (zone: string) => void;
  /** Altezza in px dell'SVG (override del default CSS di 360px). */
  heightPx?: number;
}

/** Opacità di default del fill per una zona con fastidio. */
const DEFAULT_FILL_OPACITY = 0.92;

/**
 * Figura anatomica reale (front o back) costruita dai path di `body-muscles`,
 * con i muscoli piegati sulle 22 zone del backend (vedi `muscleZoneMap`).
 * Ogni zona con un fastidio è colorata per intensità; l'hover evidenzia
 * l'intera zona (tutti i muscoli che vi appartengono).
 *
 * NB: il drop-shadow va sull'`<svg>` via CSS, MAI su un `<g filter>` interno —
 * un filtro su un gruppo che avvolge i path rompe l'hit-testing del mouse.
 */
const AnatomyFigure: FC<Props> = ({
  view,
  zoneIntensity,
  zoneOpacity,
  hoveredZone,
  onHoverZone,
  onSelectZone,
  heightPx,
}) => {
  const muscles: AnatomyMuscle[] = view === 'front' ? FRONT_ANATOMY : BACK_ANATOMY;

  return (
    <svg
      className={styles.body}
      viewBox={ANATOMY_VIEWBOX[view]}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Corpo ${view === 'front' ? 'frontale' : 'posteriore'}`}
      style={heightPx ? { height: heightPx } : undefined}
    >
      {muscles.map((m) => {
        const { zone } = m;
        const intensity = zone ? zoneIntensity.get(zone) : undefined;
        const hasComplaint = intensity !== undefined;
        const isHovered = !!zone && hoveredZone === zone;

        // Fill/stroke dinamici → eccezione inline ammessa dalle convenzioni:
        // colore intensità se c'è un fastidio, altrimenti neutro; l'hover
        // schiarisce il neutro ed evidenzia il contorno.
        const fill = hasComplaint
          ? getIntensityColor(intensity as number)
          : isHovered
          ? 'var(--c-divider)'
          : 'var(--c-chip-bg)';
        const fillOpacity = hasComplaint
          ? zoneOpacity?.get(zone as string) ?? DEFAULT_FILL_OPACITY
          : 1;

        return (
          <path
            key={m.id}
            className={styles.muscle}
            d={m.path}
            data-interactive={zone ? 'true' : undefined}
            fill={fill}
            fillOpacity={fillOpacity}
            stroke={isHovered ? 'var(--c-text-0)' : 'var(--c-card-border)'}
            strokeWidth={isHovered ? 0.4 : 0.14}
            onMouseEnter={zone ? () => onHoverZone(zone) : undefined}
            onMouseLeave={zone ? () => onHoverZone(null) : undefined}
            onClick={zone && onSelectZone ? () => onSelectZone(zone) : undefined}
          >
            <title>{zone ? ZONE_LABELS[zone] ?? m.name : m.name}</title>
          </path>
        );
      })}
    </svg>
  );
};

export default AnatomyFigure;
